import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, MenuItem,
  TextField, CircularProgress, IconButton
} from '@mui/material';
import { Add, Visibility, Payment } from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const statusColor: Record<string, any> = { draft: 'default', issued: 'primary', paid: 'success', partial: 'warning', cancelled: 'error' };

const Billing = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [patients, setPatients] = useState<any[]>([]);
  const [payAmount, setPayAmount] = useState(0);
  const [payMethod, setPayMethod] = useState('cash');
  const [items, setItems] = useState([{ description: '', quantity: 1, unitPrice: 0 }]);
  const [patientId, setPatientId] = useState('');
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);

  const load = async () => {
    try {
      const res = await api.get('/invoices', { params: { limit: 50 } });
      setInvoices(res.data.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (open) api.get('/patients', { params: { limit: 200 } }).then(r => setPatients(r.data.data));
  }, [open]);

  const addItem = () => setItems(i => [...i, { description: '', quantity: 1, unitPrice: 0 }]);
  const removeItem = (idx: number) => setItems(i => i.filter((_, n) => n !== idx));
  const setItemField = (idx: number, k: string, v: any) =>
    setItems(i => i.map((item, n) => n === idx ? { ...item, [k]: v } : item));

  const subtotal = items.reduce((s, i) => s + i.unitPrice * i.quantity, 0);
  const total = subtotal - discount + (subtotal * tax / 100);

  const handleCreate = async () => {
    try {
      await api.post('/invoices', {
        patient: patientId, items, discount, tax,
        status: 'issued', dueDate: new Date(Date.now() + 7 * 86400000)
      });
      toast.success('Invoice created');
      setOpen(false);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const handlePayment = async () => {
    try {
      await api.put(`/invoices/${selectedInvoice._id}/payment`, { paidAmount: payAmount, paymentMethod: payMethod });
      toast.success('Payment recorded');
      setPayOpen(false);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Payment failed');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Billing & Invoices</Typography>
          <Typography variant="body2" color="text.secondary">{invoices.length} invoices</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)} sx={{ borderRadius: 2 }}>
          New Invoice
        </Button>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9fafb' }}>
                  {['Invoice #', 'Patient', 'Total', 'Paid', 'Due', 'Status', 'Date', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, py: 1.5 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map(inv => (
                  <TableRow key={inv._id} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{inv.invoiceNumber}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2">{inv.patient?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{inv.patient?.patientId}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2" fontWeight={600}>₹{inv.totalAmount?.toLocaleString()}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="success.main">₹{inv.paidAmount?.toLocaleString()}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color={inv.dueAmount > 0 ? 'error.main' : 'text.secondary'}>₹{inv.dueAmount?.toLocaleString()}</Typography></TableCell>
                    <TableCell><Chip label={inv.status} size="small" color={statusColor[inv.status]} /></TableCell>
                    <TableCell><Typography variant="caption">{new Date(inv.createdAt).toLocaleDateString()}</Typography></TableCell>
                    <TableCell>
                      <IconButton size="small"><Visibility fontSize="small" /></IconButton>
                      {inv.status !== 'paid' && (
                        <IconButton size="small" onClick={() => { setSelectedInvoice(inv); setPayAmount(inv.dueAmount); setPayOpen(true); }}>
                          <Payment fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {invoices.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>No invoices found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Create Invoice Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>Create Invoice</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField select fullWidth label="Patient" size="small" value={patientId} onChange={e => setPatientId(e.target.value)}>
                {patients.map(p => <MenuItem key={p._id} value={p._id}>{p.name} ({p.patientId})</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" mb={1}>Line Items</Typography>
              {items.map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField size="small" label="Description" sx={{ flex: 3 }} value={item.description}
                    onChange={e => setItemField(idx, 'description', e.target.value)} />
                  <TextField size="small" label="Qty" type="number" sx={{ flex: 1 }} value={item.quantity}
                    onChange={e => setItemField(idx, 'quantity', Number(e.target.value))} />
                  <TextField size="small" label="Price (₹)" type="number" sx={{ flex: 1 }} value={item.unitPrice}
                    onChange={e => setItemField(idx, 'unitPrice', Number(e.target.value))} />
                  <Button size="small" color="error" onClick={() => removeItem(idx)} disabled={items.length === 1}>✕</Button>
                </Box>
              ))}
              <Button size="small" onClick={addItem} variant="outlined" sx={{ mt: 1 }}>+ Add Item</Button>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Discount (₹)" type="number" value={discount} onChange={e => setDiscount(Number(e.target.value))} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Tax (%)" type="number" value={tax} onChange={e => setTax(Number(e.target.value))} /></Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1.5, bgcolor: '#f0f4f8', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                <Typography variant="h6" fontWeight={700}>₹{total.toLocaleString()}</Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} sx={{ borderRadius: 2 }}>Create Invoice</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>Record Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Due Amount: ₹{selectedInvoice?.dueAmount?.toLocaleString()}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Payment Amount (₹)" type="number" value={payAmount} onChange={e => setPayAmount(Number(e.target.value))} /></Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label="Payment Method" value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                {['cash', 'card', 'upi', 'insurance', 'online'].map(m => <MenuItem key={m} value={m}>{m.toUpperCase()}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePayment} sx={{ borderRadius: 2 }}>Record Payment</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billing;
